// 共用工具函式 (日期 / 字串 / 數字 / 檔案下載)
// 元件內: inject('common') 或 this.$common 或 window.common

import { myAjax } from './api.js'
import { ui } from './ui.js'

const Common = {
  // 字串是否為空 (含 trim + 非字串容錯)
  stringEmpty(str) {
    try {
      return (!str || str.trim().length === 0)
    } catch {
      return (!str || str.length === 0)
    }
  },

  // 西元 → 民國 (yyy/MM/dd)
  toROCDate(date, hasTime = false) {
    if (!date) return ''
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return ''
    const year = d.getFullYear() - 1911
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    let s = `${year}/${m}/${day}`
    if (hasTime) {
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      s += ` ${hh}:${mm}`
    }
    return s
  },

  // 民國 → 西元 (yyy/MM/dd → Date)
  fromROCDate(roc) {
    if (!roc) return null
    const m = /^(\d{2,3})\/(\d{1,2})\/(\d{1,2})$/.exec(roc)
    if (!m) return null
    const y = parseInt(m[1], 10) + 1911
    return new Date(y, parseInt(m[2], 10) - 1, parseInt(m[3], 10))
  },

  // 西元 yyyy-MM-dd (hasTime 時加 HH:mm:ss)
  toDate(date, hasTime = false) {
    if (!date) return ''
    const d = new Date(date)
    if (Number.isNaN(d.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    let s = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    if (hasTime) s += ` ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    return s
  },

  // 計算兩日期間的天數
  calDays(startDate, endDate) {
    const dt1 = new Date(startDate)
    const dt2 = new Date(endDate)
    return ((dt2 - dt1) / (1000 * 60 * 60 * 24))
  },

  // 是否為數值
  isNumber(d) {
    return (typeof d === 'number' || d instanceof Number) || isFinite(d)
  },

  // 千分位
  thousands(n) {
    if (n === null || n === undefined || n === '') return ''
    return Number(n).toLocaleString('en-US')
  },

  // 簡單的物件深拷貝
  clone(obj) {
    return JSON.parse(JSON.stringify(obj))
  },

  // 檔案下載 GET
  // 後端回 blob,Content-Type 判斷:
  //   - application/json → 解析 message 後提示 (下載失敗)
  //   - 其他 → 從 Content-Disposition 取 filename 並觸發下載
  async dnFile(action, funcCallback) {
    return downloadBlob(() => myAjax.get(action, { responseType: 'blob' }), funcCallback)
  },

  // 檔案下載 POST（2026-10-27，公開價格查詢匯出要帶整份結果，GET 放不下）
  // body 直接送實體本體；回應的判斷與存檔跟 dnFile 同一套
  async dnFilePost(action, body, funcCallback) {
    return downloadBlob(() => myAjax.post(action, body, { responseType: 'blob' }), funcCallback)
  },

  // 存檔：後端放在 JSON 裡回來的檔案（base64，2026-11-03 匯入失敗的項目）。回 true／false
  saveBase64File(base64, fileName, mime = 'application/octet-stream') {
    try {
      const binary = atob(base64 || '')
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
      saveBlob(new Blob([bytes], { type: mime }), fileName)
      return true
    } catch (e) {
      console.error('[saveBase64File]', e)
      return false
    }
  },
}

// 用 <a download> 把 blob 存成檔案（downloadBlob／saveBase64File 共用）
function saveBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// dnFile／dnFilePost 共用：後端回 JSON 就是失敗訊息，否則依 Content-Disposition 的檔名存檔
async function downloadBlob(request, funcCallback) {
  try {
    const resp = await request()
    const blob = new Blob([resp.data])
    const contentType = resp.headers['content-type'] || ''
    if (contentType.indexOf('application/json') >= 0) {
      const text = await blob.text()
      try {
        const data = JSON.parse(text)
        ui.toast(data.message || data.msg || '下載失敗', 'error')
      } catch {
        ui.toast('下載失敗', 'error')
      }
      funcCallback?.(false)
      return
    }
    let saveFilename = null
    const cd = decodeURI(resp.headers['content-disposition'] || '')
    let parts = cd.split("filename*=UTF-8''")
    if (parts.length === 2) {
      saveFilename = parts[1]
    } else {
      parts = cd.split('filename=')
      if (parts.length === 2) saveFilename = parts[1].replace(/^"|"$/g, '')
    }
    if (!saveFilename) {
      console.warn('[dnFile] saveFilename is null')
      funcCallback?.(false)
      return
    }
    saveBlob(blob, saveFilename)
    funcCallback?.(true)
  } catch (e) {
    console.error('[dnFile]', e)
    funcCallback?.(false)
  }
}

export default Common
